import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { atencionesAPI, actasDiariasAPI } from '../utils/api';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Save, X, Calendar, Clock, FileText, Mail, Printer, 
  CheckCircle, Loader2, ExternalLink, AlertCircle, Paperclip, Trash2,
  Send, ChevronDown, MessageCircle
} from 'lucide-react';
import VoiceInput from './VoiceInput';
import { renderSupervisorSignatureHtml, renderTestigoInfoHtml } from '../utils/sealUtils';

const ActaDiariaDialog = ({ 
  atencionId, 
  solicitudAtencion, 
  requerimiento,
  atencion,
  actaId = null, // If provided, we're editing
  platformConfig: parentPlatformConfig = null, // Config passed from parent
  open, 
  onClose, 
  onCreated,
  onUpdated
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [acta, setActa] = useState(null);
  const [platformConfig, setPlatformConfig] = useState(parentPlatformConfig || {});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(false);
  const [showSendMenu, setShowSendMenu] = useState(false);
  const [sealData, setSealData] = useState(null);
  const [sealLoading, setSealLoading] = useState(false);
  const sendMenuRef = React.useRef(null);
  
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    hora_inicio: '',
    hora_fin: '',
    descripcion: ''
  });

  // Load platform config if not passed from parent
  useEffect(() => {
    if (parentPlatformConfig) {
      setPlatformConfig(parentPlatformConfig);
      return;
    }
    
    const loadConfig = async () => {
      try {
        const response = await api.get('/configuracion/general');
        if (response.data) {
          setPlatformConfig(response.data);
        }
      } catch (error) {
        console.error('Error loading config:', error);
      }
    };
    loadConfig();
  }, [parentPlatformConfig]);

  // Load acta if editing
  useEffect(() => {
    if (actaId && open) {
      loadActa();
    } else if (!actaId && open) {
      // Reset form for new acta
      setActa(null);
      setIsEditMode(false);
      setFormData({
        fecha: new Date().toISOString().split('T')[0],
        hora_inicio: '',
        hora_fin: '',
        descripcion: ''
      });
    }
  }, [actaId, open]);

  const loadActa = async () => {
    setLoading(true);
    try {
      const response = await actasDiariasAPI.get(actaId);
      setActa(response.data);
      setIsEditMode(true);
      setFormData({
        fecha: response.data.fecha || '',
        hora_inicio: response.data.hora_inicio || '',
        hora_fin: response.data.hora_fin || '',
        descripcion: response.data.descripcion || ''
      });
      
      // Fetch seal data if acta is approved
      const acta_estado = response.data.acta_estado || '';
      if (acta_estado.startsWith('aprobado')) {
        setSealLoading(true);
        try {
          const sealResp = await api.get(`/seal/acta_diaria/${actaId}`);
          if (sealResp.data?.aprobado) {
            setSealData(sealResp.data);
          }
        } catch (sealErr) {
          console.warn('No se pudo obtener seal data:', sealErr);
        } finally {
          setSealLoading(false);
        }
      } else {
        setSealData(null);
      }
    } catch (error) {
      toast.error('Error al cargar el acta diaria');
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = acta?.estado === 'terminado';
  const canSendEmail = acta && acta.estado !== 'terminado';
  const hasDocument = acta && (acta.acta_url || acta.acta_estado);
  const canTerminate = acta && acta.estado !== 'terminado' && hasDocument && !['enviado_correo', 'enviado_whatsapp'].includes(acta.acta_estado);
  const isSending = sendingEmail || sendingWhatsapp;
  const whatsappEnabled = platformConfig?.aprobar_acta_diaria_whatsapp;

  // Close send menu on outside click
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (sendMenuRef.current && !sendMenuRef.current.contains(e.target)) {
        setShowSendMenu(false);
      }
    };
    if (showSendMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSendMenu]);

  const validateForm = () => {
    if (!formData.hora_inicio) {
      toast.error('La hora de inicio es obligatoria');
      return false;
    }
    if (!formData.hora_fin) {
      toast.error('La hora de fin es obligatoria');
      return false;
    }
    if (!formData.descripcion?.trim()) {
      toast.error('La descripción es obligatoria');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (acta) {
        // Update existing
        const response = await actasDiariasAPI.update(acta.id, formData);
        setActa(response.data);
        toast.success('Acta diaria actualizada correctamente');
        onUpdated && onUpdated(response.data);
      } else {
        // Create new
        const response = await atencionesAPI.createActaDiaria(
          atencionId,
          solicitudAtencion.id,
          formData
        );
        setActa(response.data);
        setIsEditMode(true);
        toast.success('Acta diaria creada correctamente');
        onCreated && onCreated(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el acta diaria');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!acta) return;
    
    setSendingEmail(true);
    try {
      await actasDiariasAPI.enviarCorreo(acta.id);
      toast.success('Correo enviado correctamente');
      // Reload acta to get updated estado
      await loadActa();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!acta) return;
    
    if (!atencion?.testigo_telefono) {
      toast.error('El testigo no tiene teléfono registrado. No se puede enviar por WhatsApp.');
      return;
    }

    setSendingWhatsapp(true);
    try {
      const response = await actasDiariasAPI.enviarWhatsapp(acta.id);
      
      if (response.data?.success) {
        if (response.data.sendpulse_success) {
          toast.success(`WhatsApp enviado a ${response.data.destinatario} (${response.data.telefono})`);
        } else {
          toast.warning('Acta registrada como enviada, pero SendPulse reportó un error. Contacte al administrador.');
        }
        // Reload acta to get updated estado
        await loadActa();
      } else {
        toast.error(response.data?.message || 'Error al enviar por WhatsApp');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar por WhatsApp');
    } finally {
      setSendingWhatsapp(false);
    }
  };

  // Handle file upload for acta diaria escaneada
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !acta) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos PDF, JPG o PNG');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar los 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/actas-diarias/${acta.id}/upload-documento`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento adjuntado correctamente');
      await loadActa();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploadingFile(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Delete attached document or email acta
  const handleDeleteDocument = async (type) => {
    if (!acta) return;
    
    const confirmMsg = type === 'archivo' 
      ? '¿Está seguro de eliminar el documento adjunto?' 
      : '¿Está seguro de eliminar el acta enviada por correo?';
    
    if (!window.confirm(confirmMsg)) return;

    setDeletingDocument(true);
    try {
      await api.delete(`/actas-diarias/${acta.id}/documento?type=${type}`);
      toast.success('Documento eliminado correctamente');
      await loadActa();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el documento');
    } finally {
      setDeletingDocument(false);
    }
  };

  // Get status badge for acta_estado
  const getActaEstadoBadge = (estado) => {
    switch (estado) {
      case 'enviado_correo':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Enviado por correo</Badge>;
      case 'aprobado_correo':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Aprobado por correo</Badge>;
      case 'rechazado_correo':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rechazado por correo</Badge>;
      case 'enviado_whatsapp':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Enviado por WhatsApp</Badge>;
      case 'aprobado_whatsapp':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Aprobado WhatsApp</Badge>;
      case 'rechazado_whatsapp':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rechazado WhatsApp</Badge>;
      default:
        return null;
    }
  };

  // Get acta view URL
  const getActaViewUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/actas-diarias/${acta?.id}/acta-view`;
  };

  const handlePrint = () => {
    if (!acta || sealLoading) return;
    
    // Generate printable report (same as Inspección format)
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Acta Diaria - ${acta.codigo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px; 
            line-height: 1.4;
            padding: 20px;
            color: #333;
          }
          .header-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 2px solid #9d8272;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header-logo {
            max-height: 50px;
            max-width: 120px;
            object-fit: contain;
          }
          .header-title {
            text-align: center;
            flex: 1;
          }
          .header-title h1 {
            font-size: 18px;
            color: #9d8272;
            margin-bottom: 5px;
          }
          .header-title p {
            font-size: 12px;
            color: #666;
          }
          .header-spacer {
            width: 120px;
          }
          .section { 
            margin-bottom: 15px; 
            border: 1px solid #ddd; 
            border-radius: 5px;
            overflow: hidden;
          }
          .section-title { 
            background: #f5f5f5; 
            padding: 8px 12px; 
            font-weight: bold; 
            font-size: 12px;
            border-bottom: 1px solid #ddd;
            color: #9d8272;
          }
          .section-content { padding: 12px; }
          .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
          .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .field { margin-bottom: 8px; }
          .field-label { font-size: 9px; color: #888; text-transform: uppercase; }
          .field-value { font-weight: 500; }
          .description-box {
            background: #f9f9f9;
            padding: 15px;
            border-radius: 5px;
            margin-top: 10px;
          }
          .signatures { 
            margin-top: 40px; 
            display: grid; 
            grid-template-columns: repeat(2, 1fr); 
            gap: 40px;
            align-items: end;
          }
          .signature-box { 
            text-align: center;
          }
          .signature-label { font-size: 10px; color: #666; }
          .footer { 
            margin-top: 30px; 
            text-align: center; 
            font-size: 9px; 
            color: #888;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          @media print {
            body { padding: 10px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <div class="header-logo-container">
            ${platformConfig?.logo_url 
              ? `<img src="${platformConfig.logo_url}" alt="${platformConfig.company_name || 'Logo'}" class="header-logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
                 <span style="font-weight: bold; color: #9d8272; font-size: 14px; display: none;">${platformConfig.company_name || 'IMAGINA'}</span>`
              : `<span style="font-weight: bold; color: #9d8272; font-size: 14px;">${platformConfig?.company_name || 'IMAGINA'}</span>`
            }
          </div>
          <div class="header-title">
            <h1>ACTA DIARIA ${acta.codigo}</h1>
            <p>Requerimiento #${requerimiento?.codigo} | ${requerimiento?.proyecto_nombre || 'N/A'}</p>
          </div>
          <div class="header-spacer"></div>
        </div>

        <div class="section">
          <div class="section-title">📋 Información del Requerimiento</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Proyecto</div>
                <div class="field-value">${requerimiento?.proyecto_nombre || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Unidad/Depto</div>
                <div class="field-value">${requerimiento?.departamento || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Etapa</div>
                <div class="field-value">${requerimiento?.etapa || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Código</div>
                <div class="field-value">#${requerimiento?.codigo || '-'}</div>
              </div>
            </div>
            <div class="grid-2" style="margin-top: 10px;">
              <div class="field">
                <div class="field-label">Titular</div>
                <div class="field-value">${requerimiento?.cliente_nombre || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Teléfono Titular</div>
                <div class="field-value">${requerimiento?.cliente_telefono || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">👤 Datos del Testigo</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Nombre</div>
                <div class="field-value">${atencion?.testigo_nombre || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Doc. Identidad</div>
                <div class="field-value">${atencion?.testigo_dni || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Teléfono</div>
                <div class="field-value">${atencion?.testigo_telefono || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Correo</div>
                <div class="field-value">${atencion?.testigo_email || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">🔧 Datos de la Solicitud</div>
          <div class="section-content">
            <div class="grid">
              <div class="field">
                <div class="field-label">Código</div>
                <div class="field-value">${solicitudAtencion?.codigo || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Categoría</div>
                <div class="field-value">${solicitudAtencion?.solicitud?.categoria || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Lugar</div>
                <div class="field-value">${solicitudAtencion?.solicitud?.lugar || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Item</div>
                <div class="field-value">${solicitudAtencion?.solicitud?.item || '-'}</div>
              </div>
            </div>
            <div class="grid-2" style="margin-top: 10px;">
              <div class="field">
                <div class="field-label">Falla</div>
                <div class="field-value">${solicitudAtencion?.solicitud?.falla || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Estado</div>
                <div class="field-value">${solicitudAtencion?.estado?.replace(/_/g, ' ') || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📅 Datos de la Atención Diaria</div>
          <div class="section-content">
            <div class="grid-3">
              <div class="field">
                <div class="field-label">Fecha</div>
                <div class="field-value">${acta.fecha || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Hora Inicio</div>
                <div class="field-value">${acta.hora_inicio || '-'}</div>
              </div>
              <div class="field">
                <div class="field-label">Hora Fin</div>
                <div class="field-value">${acta.hora_fin || '-'}</div>
              </div>
            </div>
            <div class="description-box">
              <div class="field-label">Descripción del trabajo realizado</div>
              <p style="margin-top: 8px;">${acta.descripcion || 'Sin descripción'}</p>
            </div>
          </div>
        </div>

        <div style="margin-top: 20px; padding: 12px 15px; border-top: 1px solid #e0e0e0;">
          <p style="font-size: 9px; font-weight: 600; color: #aaaaaa; margin: 0 0 6px 0;">Notas</p>
          <ol style="font-size: 8px; color: #aaaaaa; margin: 0; padding-left: 16px; line-height: 1.5;">
            <li style="margin-bottom: 4px;">Por la presente, informo a ${platformConfig?.company_name || platformConfig.company_name || 'IMAGINA'} que cuento con la autorización expresa del propietario para firmar y dar validez a la información consignada en este documento.</li>
            <li>Por la presente, al proporcionar mis datos autorizo de manera expresa a ${platformConfig?.company_name || platformConfig.company_name || 'IMAGINA'} a almacenarlos y hacer uso de los mismos con la finalidad de enviar información, publicidad, encuestas y comunicaciones con fines estratégicos de marketing físico y digital.</li>
          </ol>
        </div>

        <div class="signatures">
          <div class="signature-box">
            ${renderSupervisorSignatureHtml(requerimiento)}
          </div>
          <div class="signature-box">
            ${(() => {
              const estado = acta?.acta_estado || '';
              const actaAprobada = ['aceptado', 'aprobado_correo', 'aprobado_whatsapp'].includes(estado);
              return renderTestigoInfoHtml(
                { nombre: atencion?.testigo_nombre, dni: atencion?.testigo_dni },
                sealData,
                false,
                actaAprobada
              );
            })()}
          </div>
        </div>

        <div class="footer">
          <p>Documento generado el ${new Date().toLocaleString('es-PE')} | ${platformConfig.platform_name || 'Sistema de Postventa'} ${platformConfig.company_name || 'IMAGINA'}</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } else {
      toast.error('No se pudo abrir la ventana de impresión');
    }
  };

  const handleTerminate = async () => {
    if (!acta) return;
    
    setTerminating(true);
    try {
      await actasDiariasAPI.terminar(acta.id);
      toast.success('Acta diaria terminada correctamente');
      await loadActa();
      onUpdated && onUpdated({ ...acta, estado: 'terminado' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al terminar el acta');
    } finally {
      setTerminating(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const config = {
      pendiente: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendiente' },
      terminado: { bg: 'bg-green-100', text: 'text-green-700', label: 'Terminado' },
      rechazado: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rechazado' }
    };
    const { bg, text, label } = config[estado] || config.pendiente;
    return <Badge className={`${bg} ${text}`}>{label}</Badge>;
  };

  // Generate folio for title
  const getFolio = () => {
    if (acta?.codigo) return acta.codigo;
    // Generate preview folio for new acta
    const reqCodigo = requerimiento?.codigo || '00000';
    const solCorrelativo = solicitudAtencion?.codigo?.split('-')[1] || '01';
    return `AD${reqCodigo}-${solCorrelativo}-??`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        preventMobileClose
      >
        <DialogHeader className="border-b border-[#E8E8E7] pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <DialogTitle className="text-base sm:text-lg font-bold text-[#2D2D2D] flex items-center gap-2">
              <FileText size={20} className="text-[#9d8272] flex-shrink-0" />
              <span className="truncate">{isEditMode ? `Acta Diaria ${getFolio()}` : `Nueva Acta Diaria ${getFolio()}`}</span>
            </DialogTitle>
            {acta && (
              <div className="flex items-center gap-2 flex-wrap">
                {getEstadoBadge(acta.estado)}
                {acta.acta_estado === 'aprobado_correo' && (
                  <Badge className="bg-green-100 text-green-700">Aprobado por correo</Badge>
                )}
                {acta.acta_estado === 'rechazado_correo' && (
                  <Badge className="bg-red-100 text-red-700">Rechazado por correo</Badge>
                )}
                {acta.acta_estado === 'enviado_whatsapp' && (
                  <Badge className="bg-blue-100 text-blue-700">Enviado WhatsApp</Badge>
                )}
                {acta.acta_estado === 'aprobado_whatsapp' && (
                  <Badge className="bg-green-100 text-green-700">Aprobado WhatsApp</Badge>
                )}
                {acta.acta_estado === 'rechazado_whatsapp' && (
                  <Badge className="bg-red-100 text-red-700">Rechazado WhatsApp</Badge>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#9d8272]" />
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Date and Time Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Calendar size={12} />
                  Fecha *
                </Label>
                <Input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  className="border-[#c5b8a2] text-sm"
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Clock size={12} />
                  Hora Inicio *
                </Label>
                <Input
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                  className="border-[#c5b8a2] text-sm"
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Clock size={12} />
                  Hora Fin *
                </Label>
                <Input
                  type="time"
                  value={formData.hora_fin}
                  onChange={(e) => setFormData({ ...formData, hora_fin: e.target.value })}
                  className="border-[#c5b8a2] text-sm"
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Estado Field (Read-only) */}
            <div>
              <Label className="text-xs flex items-center gap-1">
                <AlertCircle size={12} />
                Estado *
              </Label>
              <Input
                value={acta?.estado ? acta.estado.charAt(0).toUpperCase() + acta.estado.slice(1) : 'Pendiente'}
                className="border-[#c5b8a2] text-sm bg-gray-50"
                disabled
                readOnly
              />
            </div>

            {/* Description Field */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Descripción del trabajo realizado *</Label>
                {!isReadOnly && (
                  <VoiceInput
                    onTranscript={(text, isFinal) => {
                      if (isFinal) {
                        setFormData(prev => ({
                          ...prev,
                          descripcion: prev.descripcion ? `${prev.descripcion} ${text}` : text
                        }));
                      }
                    }}
                  />
                )}
              </div>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="mt-1 border-[#c5b8a2] text-sm"
                rows={4}
                placeholder="Describa el trabajo realizado en el día..."
                disabled={isReadOnly}
              />
            </div>

            {/* Document Section - Only show in edit mode */}
            {isEditMode && acta && (
              <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-[#E8E8E7]">
                <Label className="text-xs font-semibold text-[#9d8272]">Adjuntar acta diaria firmada o presionar botón Enviar</Label>
                
                <div className="flex flex-col gap-3">
                  {/* Show file upload button or uploaded file link */}
                  {!acta.acta_url && !acta.acta_estado ? (
                    // Show Adjuntar button
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="file"
                        id="acta-file-upload"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                        disabled={uploadingFile || isReadOnly}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('acta-file-upload').click()}
                        disabled={uploadingFile || isReadOnly}
                        className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white"
                      >
                        {uploadingFile ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <Paperclip size={14} className="mr-1" />
                        )}
                        {uploadingFile ? 'Subiendo...' : 'Adjuntar'}
                      </Button>
                      <span className="text-xs text-gray-500">PDF, JPG o PNG (máx. 10MB)</span>
                    </div>
                  ) : (
                    // Show document link(s)
                    <div className="space-y-2">
                      {/* Attached file */}
                      {acta.acta_url && (
                        <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                          <FileText size={16} className="text-[#9d8272] flex-shrink-0" />
                          <a
                            href={acta.acta_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex items-center gap-1 min-w-0 truncate"
                          >
                            Acta diaria {acta.codigo}
                            <ExternalLink size={12} className="flex-shrink-0" />
                          </a>
                          <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100 flex-shrink-0">Archivo adjunto</Badge>
                          {!isReadOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument('archivo')}
                              disabled={deletingDocument}
                              className="flex-shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                            >
                              {deletingDocument ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Email acta or WhatsApp acta */}
                      {acta.acta_estado && (
                        <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                          {acta.acta_estado.includes('whatsapp') ? (
                            <MessageCircle size={16} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <Mail size={16} className="text-[#9d8272] flex-shrink-0" />
                          )}
                          <a
                            href={getActaViewUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex items-center gap-1 min-w-0 truncate"
                          >
                            Acta diaria {acta.codigo}
                            <ExternalLink size={12} className="flex-shrink-0" />
                          </a>
                          {getActaEstadoBadge(acta.acta_estado)}
                          {!isReadOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument('correo')}
                              disabled={deletingDocument}
                              className="flex-shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                            >
                              {deletingDocument ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <TooltipProvider>
              <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-[#E8E8E7]">
                {!isReadOnly && (
                  <>
                    {/* Save Button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={handleSave}
                          disabled={saving}
                          className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                        >
                          {saving ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Save size={16} />
                          )}
                          <span className="hidden sm:inline ml-1">{saving ? 'Guardando...' : 'Guardar'}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Guardar los datos del acta diaria</p>
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}

                {/* Buttons that appear after saving */}
                {acta && (
                  <>
                    {/* Send Button - Dropdown when WhatsApp enabled */}
                    {canSendEmail && (
                      whatsappEnabled ? (
                        <div className="relative" ref={sendMenuRef}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                disabled={isSending}
                                onClick={() => setShowSendMenu(!showSendMenu)}
                                className="border-blue-500 text-blue-500 disabled:opacity-50 disabled:border-gray-300 disabled:text-gray-400"
                                data-testid="acta-diaria-send-dropdown-btn"
                              >
                                {isSending ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Send size={16} />
                                )}
                                <span className="hidden sm:inline ml-1">Enviar</span>
                                <ChevronDown size={12} className="ml-1" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Enviar acta al testigo</p>
                            </TooltipContent>
                          </Tooltip>

                          {showSendMenu && (
                            <div className="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] overflow-hidden">
                              <button
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 text-left transition-colors"
                                onClick={() => { setShowSendMenu(false); handleSendEmail(); }}
                                disabled={sendingEmail}
                                data-testid="acta-diaria-send-by-email-option"
                              >
                                <Mail size={15} className="text-blue-600" />
                                <span>Correo</span>
                              </button>
                              <div className="border-t border-gray-100" />
                              <button
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 text-left transition-colors"
                                onClick={() => { setShowSendMenu(false); handleSendWhatsApp(); }}
                                disabled={sendingWhatsapp}
                                data-testid="acta-diaria-send-by-whatsapp-option"
                              >
                                <MessageCircle size={15} className="text-green-600" />
                                <span>WhatsApp</span>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={handleSendEmail}
                              disabled={sendingEmail}
                              className="border-blue-500 text-blue-500"
                              data-testid="acta-diaria-send-email-btn"
                            >
                              {sendingEmail ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Mail size={16} />
                              )}
                              <span className="hidden sm:inline ml-1">{sendingEmail ? 'Enviando...' : 'Enviar'}</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{atencion?.testigo_email ? `Enviar informe por correo a ${atencion.testigo_email}` : 'No hay correo de testigo configurado'}</p>
                          </TooltipContent>
                        </Tooltip>
                      )
                    )}

                    {/* Print Button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          onClick={handlePrint}
                          disabled={sealLoading}
                          className="border-gray-500 text-gray-500"
                        >
                          {sealLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                          <span className="hidden sm:inline ml-1">{sealLoading ? 'Cargando...' : 'Imprimir'}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{sealLoading ? 'Cargando sello digital...' : 'Generar informe para impresión con firmas'}</p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Terminate Button */}
                    {acta && acta.estado !== 'terminado' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              onClick={handleTerminate}
                              disabled={terminating || !hasDocument}
                              className="bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              {terminating ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <CheckCircle size={16} />
                              )}
                              <span className="hidden sm:inline ml-1">{terminating ? 'Terminando...' : 'Terminar'}</span>
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {hasDocument && ['enviado_correo', 'enviado_whatsapp'].includes(acta?.acta_estado) ? (
                            <p>Esperando aprobación del testigo</p>
                          ) : hasDocument ? (
                            <p>Marcar el acta como terminada (no se podrá modificar)</p>
                          ) : (
                            <p>Debe adjuntar un documento o enviar el acta por correo/WhatsApp antes de terminar</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            </TooltipProvider>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActaDiariaDialog;
